import { defaultLocale } from "../../i18n/config";
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from "../languages";

export type PublicRouteSchemaType =
  | "home"
  | "marketing"
  | "about"
  | "contact"
  | "legal"
  | "search-landing"
  | "blog";

export interface PublicRouteConfig {
  canonicalPath: string;
  seoKey: string;
  namespaces: readonly string[];
  priority: number;
  changeFrequency:
    | "always"
    | "hourly"
    | "daily"
    | "weekly"
    | "monthly"
    | "yearly"
    | "never";
  lastModified: string;
  schemaType: PublicRouteSchemaType;
  ogImage: string;
  searchPageKey?: string;
  relatedBlogSlugs?: readonly string[];
}

const MARKETING_REFRESHED_AT = "2026-04-07T00:00:00.000Z";
const LEGAL_REFRESHED_AT = "2026-03-07T00:00:00.000Z";
const CHANGELOG_REFRESHED_AT = "2026-04-07T00:00:00.000Z";
export const PUBLIC_SEO_RELEASE_ISO = MARKETING_REFRESHED_AT;

export const PUBLIC_CANONICAL_ORIGIN = "https://vidseeds.ai";
export const PUBLIC_CANONICAL_HOST = "vidseeds.ai";
export const PUBLIC_LEGACY_HOSTS = [
  "www.vidseeds.ai",
  "prod2.vidseeds.ai",
] as const;
export const PUBLIC_STAGE_HOSTS = ["stagev2.vidseeds.ai"] as const;
export const PUBLIC_DEFAULT_OG_IMAGE = "/blog-images/default.jpg";
// 7 days. Safe to hold long because every deploy purges the Cloudflare cache
// via `bun run cloudflare:purge:prod` / `:stage`. Longer TTL = fewer origin
// hits for stable marketing pages and higher cache-hit ratio.
export const PUBLIC_HTML_CACHE_TTL_SECONDS = 60 * 60 * 24 * 7;
// 1 day for /robots.txt, /sitemap.xml, /llms.txt. These regenerate on deploy
// and are purged alongside public HTML, so a long TTL is safe.
export const PUBLIC_MACHINE_ENDPOINT_CACHE_TTL_SECONDS = 60 * 60 * 24;
export const PUBLIC_VERIFIED_CRAWLERS = [
  "Googlebot",
  "Google-InspectionTool",
  "GoogleOther",
  "bingbot",
  "AdIdxBot",
  "DuckAssistBot",
  "GPTBot",
  "ChatGPT-User",
  "OAI-SearchBot",
  "ClaudeBot",
  "Claude-User",
  "Claude-SearchBot",
  "anthropic-ai",
  "PerplexityBot",
  "CCBot",
  "Applebot",
  "meta-externalagent",
  "facebookexternalhit",
  "Bytespider",
  "cohere-ai",
] as const;

export const PUBLIC_ROBOTS_ALLOWED_AGENTS = [
  ...PUBLIC_VERIFIED_CRAWLERS,
  "Google-Extended",
] as const;

export const INDEXABLE_MARKETING_LOCALES: readonly SupportedLanguage[] = [
  "en",
  "es",
  "pt",
  "fr",
  "de",
  "ja",
  "ko",
  "zh",
  "hi",
  "ar",
  "ru",
  "id",
  "it",
  "nl",
  "tr",
  "pl",
  "sv",
  "th",
  "vi",
  "nb",
] as const;

export const PUBLIC_ROUTE_ALIASES = {
  "/pricing": "/seeds",
  "/support": "/contact",
  "/terms": "/terms-of-service",
  "/privacy": "/privacy-policy",
} as const;

export const PUBLIC_AUTH_REDIRECTS = {
  "/dashboard": "/product",
  "/projects": "/product",
  "/projects/new": "/video-optimization",
  "/video-upload": "/getting-started",
  "/reoptimize": "/video-optimization",
  "/thumbnails": "/thumbnail-generator",
  "/translate": "/ai-video-translation",
  "/dubbing": "/ai-video-translation",
  "/captions": "/ai-video-translation",
  "/diagnose": "/video-optimization",
  "/precision-trim": "/video-optimization",
  "/midroll-ads": "/video-optimization",
  "/auto-clips": "/product",
  "/history": "/product",
  "/channels": "/getting-started",
  "/connect-channel": "/getting-started",
  "/settings/accounts": "/getting-started",
  "/settings/voice": "/product",
  "/channel-description-generator": "/youtube-description-generator",
  "/intelligence": "/video-optimization",
  "/why": "/video-optimization",
  "/video-ideas": "/youtube-title-generator",
} as const;

export const ROBOTS_DISALLOWED_PATHS = [
  "/api/",
  "/admin",
  "/admin/",
  "/auth/",
  "/dashboard",
  "/dashboard/",
  "/login",
  "/signup",
  "/forgot-password",
  "/reset-password",
  "/settings/",
  "/profile/",
  "/projects/",
  "/thumbnails/",
  "/translate/",
  "/auto-clips/",
  "/intelligence/",
  "/why/",
  "/history/",
  "/captions/",
  "/reoptimize/",
  "/precision-trim/",
  "/diagnose/",
  "/midroll-ads/",
  "/video-ideas/",
  "/channel-description-generator/",
  "/dubbing/",
  "/channels/",
  "/connect-channel/",
  "/labs/",
  "/tools/",
  "/health",
  "/_health",
  "/websocket/",
  "/ws/",
  "/*sort=",
  "/*filter=",
] as const;
export const PUBLIC_ROBOTS_DISALLOW_PATHS = ROBOTS_DISALLOWED_PATHS;
export const PUBLIC_CACHE_BYPASS_PATHS = ROBOTS_DISALLOWED_PATHS;

const BLOG_ROUTE_CONFIG: PublicRouteConfig = {
  canonicalPath: "/blog",
  seoKey: "blog",
  namespaces: ["Header", "Footer", "LanguageSwitcher", "Blog"],
  priority: 0.8,
  changeFrequency: "daily",
  lastModified: MARKETING_REFRESHED_AT,
  schemaType: "blog",
  ogImage: "/blog-images/welcome-to-vidseeds-blog.jpg",
};

export const PUBLIC_ROUTE_CONFIGS: readonly PublicRouteConfig[] = [
  {
    canonicalPath: "/",
    seoKey: "home",
    namespaces: [
      "Header",
      "Footer",
      "LanguageSwitcher",
      "Hero",
      "HomepageFeaturedCreators",
      "HomepageFeatureOptInDialog",
      "HomePlatforms",
      "HomeWhyDifferent",
      "HomeFeatures",
      "HomeHowItWorks",
      "HomeTrust",
      "HomepageCreatorResults",
      "HomeFinalCta",
    ],
    priority: 1,
    changeFrequency: "daily",
    lastModified: MARKETING_REFRESHED_AT,
    schemaType: "home",
    ogImage: "/blog-images/default.jpg",
  },
  {
    canonicalPath: "/about",
    seoKey: "about",
    namespaces: ["Header", "Footer", "LanguageSwitcher", "AboutPage"],
    priority: 0.7,
    changeFrequency: "monthly",
    lastModified: MARKETING_REFRESHED_AT,
    schemaType: "about",
    ogImage: "/blog-images/vidseeds-official-launch-december-2025.jpg",
  },
  {
    canonicalPath: "/alternatives",
    seoKey: "alternatives",
    namespaces: ["Header", "Footer", "LanguageSwitcher", "AlternativesPage"],
    priority: 0.7,
    changeFrequency: "monthly",
    lastModified: MARKETING_REFRESHED_AT,
    schemaType: "marketing",
    ogImage: "/blog-images/vidseeds-vs-vidiq-vs-tubebuddy.jpg",
  },
  {
    canonicalPath: "/ai-video-translation",
    seoKey: "aiVideoTranslation",
    namespaces: [
      "Header",
      "Footer",
      "LanguageSwitcher",
      "SearchLandingPages",
      "Blog",
    ],
    priority: 0.88,
    changeFrequency: "weekly",
    lastModified: MARKETING_REFRESHED_AT,
    schemaType: "search-landing",
    ogImage: "/blog-images/ai-video-translation-guide.jpg",
    searchPageKey: "aiVideoTranslation",
    relatedBlogSlugs: [
      "ai-video-translation-guide",
      "how-to-translate-youtube-videos-ai-voice-cloning",
      "multi-language-youtube-strategy",
    ],
  },
  BLOG_ROUTE_CONFIG,
  {
    canonicalPath: "/changelog",
    seoKey: "changelog",
    namespaces: ["Header", "Footer", "LanguageSwitcher", "ChangelogPage"],
    priority: 0.6,
    changeFrequency: "weekly",
    lastModified: CHANGELOG_REFRESHED_AT,
    schemaType: "marketing",
    ogImage: "/blog-images/vidseeds-official-launch-december-2025.jpg",
  },
  {
    canonicalPath: "/contact",
    seoKey: "contact",
    namespaces: ["Header", "Footer", "LanguageSwitcher", "ContactPage"],
    priority: 0.7,
    changeFrequency: "monthly",
    lastModified: MARKETING_REFRESHED_AT,
    schemaType: "contact",
    ogImage: "/blog-images/vidseeds-official-launch-december-2025.jpg",
  },
  {
    canonicalPath: "/download",
    seoKey: "download",
    namespaces: ["Header", "Footer", "LanguageSwitcher", "DownloadPage"],
    priority: 0.75,
    changeFrequency: "weekly",
    lastModified: MARKETING_REFRESHED_AT,
    schemaType: "marketing",
    ogImage: "/blog-images/vidseeds-official-launch-december-2025.jpg",
  },
  {
    canonicalPath: "/features",
    seoKey: "features",
    namespaces: ["Header", "Footer", "LanguageSwitcher", "Features"],
    priority: 0.9,
    changeFrequency: "weekly",
    lastModified: MARKETING_REFRESHED_AT,
    schemaType: "marketing",
    ogImage: "/blog-images/default.jpg",
  },
  {
    canonicalPath: "/getting-started",
    seoKey: "gettingStarted",
    namespaces: ["Header", "Footer", "LanguageSwitcher", "GettingStartedPage"],
    priority: 0.8,
    changeFrequency: "monthly",
    lastModified: MARKETING_REFRESHED_AT,
    schemaType: "marketing",
    ogImage: "/blog-images/how-to-start-youtube-channel-2026.jpg",
  },
  {
    canonicalPath: "/how-to",
    seoKey: "howTo",
    namespaces: ["Header", "Footer", "LanguageSwitcher", "HowTo"],
    priority: 0.8,
    changeFrequency: "weekly",
    lastModified: MARKETING_REFRESHED_AT,
    schemaType: "marketing",
    ogImage: "/blog-images/youtube-seo-guide-2026.jpg",
  },
  {
    canonicalPath: "/intellectual-property",
    seoKey: "intellectualProperty",
    namespaces: [
      "Header",
      "Footer",
      "LanguageSwitcher",
      "IntellectualProperty",
    ],
    priority: 0.5,
    changeFrequency: "yearly",
    lastModified: LEGAL_REFRESHED_AT,
    schemaType: "legal",
    ogImage: "/blog-images/default.jpg",
  },
  {
    canonicalPath: "/privacy-policy",
    seoKey: "privacyPolicy",
    namespaces: ["Header", "Footer", "LanguageSwitcher", "PrivacyPolicy"],
    priority: 0.5,
    changeFrequency: "yearly",
    lastModified: LEGAL_REFRESHED_AT,
    schemaType: "legal",
    ogImage: "/blog-images/default.jpg",
  },
  {
    canonicalPath: "/product",
    seoKey: "product",
    namespaces: ["Header", "Footer", "LanguageSwitcher", "ProductPage"],
    priority: 0.85,
    changeFrequency: "weekly",
    lastModified: MARKETING_REFRESHED_AT,
    schemaType: "marketing",
    ogImage: "/blog-images/default.jpg",
  },
  {
    canonicalPath: "/refund-policy",
    seoKey: "refundPolicy",
    namespaces: ["Header", "Footer", "LanguageSwitcher", "RefundPolicy"],
    priority: 0.5,
    changeFrequency: "yearly",
    lastModified: LEGAL_REFRESHED_AT,
    schemaType: "legal",
    ogImage: "/blog-images/default.jpg",
  },
  {
    canonicalPath: "/seeds",
    seoKey: "seeds",
    namespaces: [
      "Header",
      "Footer",
      "LanguageSwitcher",
      "SeedsStore",
      "Seeds",
      "SeedsCounter",
    ],
    priority: 0.75,
    changeFrequency: "weekly",
    lastModified: MARKETING_REFRESHED_AT,
    schemaType: "marketing",
    ogImage: "/blog-images/default.jpg",
  },
  {
    canonicalPath: "/terms-of-service",
    seoKey: "termsOfService",
    namespaces: ["Header", "Footer", "LanguageSwitcher", "TermsOfService"],
    priority: 0.5,
    changeFrequency: "yearly",
    lastModified: LEGAL_REFRESHED_AT,
    schemaType: "legal",
    ogImage: "/blog-images/default.jpg",
  },
  {
    canonicalPath: "/thumbnail-generator",
    seoKey: "thumbnailGenerator",
    namespaces: [
      "Header",
      "Footer",
      "LanguageSwitcher",
      "SearchLandingPages",
      "Blog",
    ],
    priority: 0.88,
    changeFrequency: "weekly",
    lastModified: MARKETING_REFRESHED_AT,
    schemaType: "search-landing",
    ogImage: "/blog-images/ai-thumbnail-generation-guide.jpg",
    searchPageKey: "thumbnailGenerator",
    relatedBlogSlugs: [
      "ai-thumbnail-generation-guide",
      "youtube-thumbnail-best-practices",
      "best-ai-tools-youtube-thumbnails-2026",
    ],
  },
  {
    canonicalPath: "/tubebuddy-alternative",
    seoKey: "tubebuddyAlternative",
    namespaces: [
      "Header",
      "Footer",
      "LanguageSwitcher",
      "SearchLandingPages",
      "Blog",
    ],
    priority: 0.82,
    changeFrequency: "weekly",
    lastModified: MARKETING_REFRESHED_AT,
    schemaType: "search-landing",
    ogImage: "/blog-images/vidseeds-vs-vidiq-vs-tubebuddy.jpg",
    searchPageKey: "tubebuddyAlternative",
    relatedBlogSlugs: [
      "vidseeds-vs-vidiq-vs-tubebuddy",
      "youtube-seo-guide-2026",
      "meaning-first-growth-strategy-2026",
    ],
  },
  {
    canonicalPath: "/video-optimization",
    seoKey: "videoOptimization",
    namespaces: [
      "Header",
      "Footer",
      "LanguageSwitcher",
      "SearchLandingPages",
      "Blog",
    ],
    priority: 0.92,
    changeFrequency: "weekly",
    lastModified: MARKETING_REFRESHED_AT,
    schemaType: "search-landing",
    ogImage: "/blog-images/meaning-first-video-optimization.jpg",
    searchPageKey: "videoOptimization",
    relatedBlogSlugs: [
      "meaning-first-video-optimization",
      "youtube-seo-guide-2026",
      "meaning-first-keyword-strategy",
    ],
  },
  {
    canonicalPath: "/vidiq-alternative",
    seoKey: "vidiqAlternative",
    namespaces: [
      "Header",
      "Footer",
      "LanguageSwitcher",
      "SearchLandingPages",
      "Blog",
    ],
    priority: 0.82,
    changeFrequency: "weekly",
    lastModified: MARKETING_REFRESHED_AT,
    schemaType: "search-landing",
    ogImage: "/blog-images/vidseeds-vs-vidiq-vs-tubebuddy.jpg",
    searchPageKey: "vidiqAlternative",
    relatedBlogSlugs: [
      "vidseeds-vs-vidiq-vs-tubebuddy",
      "youtube-seo-guide-2026",
      "meaning-first-growth-strategy-2026",
    ],
  },
  {
    canonicalPath: "/youtube-description-generator",
    seoKey: "youtubeDescriptionGenerator",
    namespaces: [
      "Header",
      "Footer",
      "LanguageSwitcher",
      "SearchLandingPages",
      "Blog",
    ],
    priority: 0.84,
    changeFrequency: "weekly",
    lastModified: MARKETING_REFRESHED_AT,
    schemaType: "search-landing",
    ogImage: "/blog-images/youtube-description-generator-guide.jpg",
    searchPageKey: "youtubeDescriptionGenerator",
    relatedBlogSlugs: [
      "youtube-description-generator-guide",
      "youtube-seo-guide-2026",
      "meaning-first-keyword-strategy",
    ],
  },
  {
    canonicalPath: "/youtube-title-generator",
    seoKey: "youtubeTitleGenerator",
    namespaces: [
      "Header",
      "Footer",
      "LanguageSwitcher",
      "SearchLandingPages",
      "Blog",
    ],
    priority: 0.84,
    changeFrequency: "weekly",
    lastModified: MARKETING_REFRESHED_AT,
    schemaType: "search-landing",
    ogImage: "/blog-images/youtube-title-optimization-tips.jpg",
    searchPageKey: "youtubeTitleGenerator",
    relatedBlogSlugs: [
      "youtube-title-optimization-tips",
      "youtube-seo-guide-2026",
      "ctr-the-promise-you-make",
    ],
  },
] as const;

export const PUBLIC_CANONICAL_PATHS = PUBLIC_ROUTE_CONFIGS.map(
  (route) => route.canonicalPath
);
export const PUBLIC_CACHEABLE_ROUTE_PATTERNS = [
  ...PUBLIC_CANONICAL_PATHS,
  "/blog/*",
] as const;

export type PublicHtmlCacheBypassCondition = {
  type: "header" | "query";
  key: string;
  value?: string;
};

export const PUBLIC_HTML_CACHE_BYPASS_CONDITIONS: readonly PublicHtmlCacheBypassCondition[] =
  [
    { type: "header", key: "rsc", value: "1" },
    { type: "header", key: "next-router-state-tree" },
    { type: "header", key: "next-router-prefetch", value: "1" },
    { type: "header", key: "next-router-segment-prefetch", value: "1" },
    { type: "query", key: "_rsc" },
  ] as const;

export function buildCloudflarePublicHtmlCacheBypassExpression(): string {
  return [
    'any(http.request.headers["rsc"][*] eq "1")',
    'any(http.request.headers["next-router-state-tree"][*] ne "")',
    'any(http.request.headers["next-router-prefetch"][*] eq "1")',
    'any(http.request.headers["next-router-segment-prefetch"][*] eq "1")',
    'http.request.uri.query contains "_rsc="',
  ].join(" or ");
}

export const PUBLIC_HTML_CACHE_AUTH_COOKIE_NAMES = [
  "better-auth.session_token",
  "__Secure-better-auth.session_token",
] as const;

export function buildCloudflareAuthCookieBypassExpression(): string {
  return PUBLIC_HTML_CACHE_AUTH_COOKIE_NAMES.map(
    (name) => `http.cookie contains "${name}="`
  ).join(" or ");
}

// Paths served either by Next.js metadata routes (app/robots.ts, app/sitemap.ts),
// route handlers (app/feed.xml/route.ts), or static files in public/ (llms.txt,
// ai.txt, humans.txt, security.txt). All are machine-readable endpoints
// consumed by crawlers (search engines, LLM indexers, security researchers).
// They change only on deploy, so a long edge TTL is safe and drops origin load.
export const PUBLIC_MACHINE_ENDPOINT_PATHS = [
  "/ai.txt",
  "/feed.xml",
  "/humans.txt",
  "/llms.txt",
  "/robots.txt",
  "/security.txt",
  "/sitemap.xml",
] as const;

export function buildCloudflareMachineEndpointPathExpression(): string {
  const list = PUBLIC_MACHINE_ENDPOINT_PATHS.map((path) => `"${path}"`).join(
    " "
  );
  return `http.request.uri.path in {${list}}`;
}

export function buildPublicHtmlCacheMissingConditions(): PublicHtmlCacheBypassCondition[] {
  return PUBLIC_HTML_CACHE_BYPASS_CONDITIONS.map((condition) => ({
    ...condition,
  }));
}

export function normalizePublicPath(pathname: string): string {
  if (!pathname) return "/";
  if (pathname === "/") return "/";
  return pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
}

export function withPublicTrailingSlash(pathname: string): string {
  const normalized = normalizePublicPath(pathname);
  return normalized === "/" ? "/" : `${normalized}/`;
}

export const ensureTrailingSlash = withPublicTrailingSlash;

function isExactOrPrefixed(pathname: string, basePath: string): boolean {
  if (basePath === "/") {
    return pathname === "/";
  }

  return pathname === basePath || pathname.startsWith(`${basePath}/`);
}

export function buildLocalizedPublicPath(
  canonicalPath: string,
  locale: SupportedLanguage
): string {
  const normalized = normalizePublicPath(canonicalPath);

  if (locale === defaultLocale) {
    return normalized;
  }

  return normalized === "/" ? `/${locale}` : `/${locale}${normalized}`;
}

export function buildPublicAbsoluteUrl(
  pathname: string,
  origin = PUBLIC_CANONICAL_ORIGIN
) {
  const normalized = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const looksLikeFile = /\.[a-z0-9]+$/i.test(normalized);
  return new URL(
    looksLikeFile ? normalized : withPublicTrailingSlash(normalized),
    origin
  ).toString();
}

export function buildCanonicalPublicPath(
  canonicalPath: string,
  locale: SupportedLanguage = defaultLocale as SupportedLanguage
) {
  return withPublicTrailingSlash(
    buildLocalizedPublicPath(canonicalPath, locale)
  );
}

export function buildCanonicalPublicUrl(
  canonicalPath: string,
  locale: SupportedLanguage = defaultLocale as SupportedLanguage,
  origin = PUBLIC_CANONICAL_ORIGIN
) {
  return buildPublicAbsoluteUrl(
    buildLocalizedPublicPath(canonicalPath, locale),
    origin
  );
}

export function getPublicRouteConfig(
  pathname: string
): PublicRouteConfig | null {
  const normalized = normalizePublicPath(pathname);

  if (isExactOrPrefixed(normalized, BLOG_ROUTE_CONFIG.canonicalPath)) {
    return BLOG_ROUTE_CONFIG;
  }

  return (
    PUBLIC_ROUTE_CONFIGS.find(
      (route) =>
        route.canonicalPath === normalized && route.canonicalPath !== "/blog"
    ) ?? null
  );
}

export function getSearchLandingRouteConfig(pathname: string) {
  const route = getPublicRouteConfig(pathname);
  if (!route?.searchPageKey) {
    return null;
  }

  return route;
}

export function getPublicPageNamespaces(pathname: string): readonly string[] {
  return (
    getPublicRouteConfig(pathname)?.namespaces ?? [
      "Header",
      "Footer",
      "LanguageSwitcher",
      // CookieConsent is in the root layout and renders on all pages (public and
      // authenticated). Seed it here so client-side useJsonTranslation never needs
      // to fetch it, avoiding 403 errors on CDN-blocked locale paths (NODE-2TT).
      "CookieConsent",
    ]
  );
}

export function isIndexableMarketingLocale(locale: string) {
  return INDEXABLE_MARKETING_LOCALES.includes(locale as SupportedLanguage);
}

export function buildPublicLanguageAlternates(canonicalPath: string) {
  const alternates: Record<string, string> = {
    "x-default": withPublicTrailingSlash(
      buildLocalizedPublicPath(
        canonicalPath,
        defaultLocale as SupportedLanguage
      )
    ),
  };

  for (const locale of INDEXABLE_MARKETING_LOCALES) {
    alternates[locale] = withPublicTrailingSlash(
      buildLocalizedPublicPath(canonicalPath, locale)
    );
  }

  return alternates;
}

export function buildIndexableLanguageAlternates(canonicalPath: string) {
  return Object.fromEntries(
    Object.entries(buildPublicLanguageAlternates(canonicalPath)).map(
      ([locale, pathname]) => [locale, buildPublicAbsoluteUrl(pathname)]
    )
  );
}

export function normalizePublicHost(host: string | null | undefined) {
  if (!host) return null;
  return host.split(":")[0]?.toLowerCase() || null;
}

export function isLocalPublicHost(host: string | null | undefined) {
  const normalized = normalizePublicHost(host);
  return normalized === "localhost" || normalized === "127.0.0.1";
}

export function isCanonicalPublicHost(host: string | null | undefined) {
  return normalizePublicHost(host) === PUBLIC_CANONICAL_HOST;
}

export function isLegacyPublicHost(host: string | null | undefined) {
  const normalized = normalizePublicHost(host);
  return Boolean(
    normalized &&
    PUBLIC_LEGACY_HOSTS.includes(
      normalized as (typeof PUBLIC_LEGACY_HOSTS)[number]
    )
  );
}

export function isStagePublicHost(host: string | null | undefined) {
  const normalized = normalizePublicHost(host);
  return Boolean(
    normalized &&
    PUBLIC_STAGE_HOSTS.includes(
      normalized as (typeof PUBLIC_STAGE_HOSTS)[number]
    )
  );
}

export function isStagingPublicHost(host: string | null | undefined) {
  return isStagePublicHost(host);
}

export function shouldNoindexPublicHost(host: string | null | undefined) {
  if (isLocalPublicHost(host) || isCanonicalPublicHost(host)) {
    return false;
  }

  return true;
}

export function getPublicAuthRedirect(pathname: string) {
  const normalized = normalizePublicPath(pathname);

  for (const [sourcePath, targetPath] of Object.entries(
    PUBLIC_AUTH_REDIRECTS
  )) {
    if (isExactOrPrefixed(normalized, sourcePath)) {
      return targetPath;
    }
  }

  return null;
}

export function resolveMarketingRedirectPath(pathname: string) {
  return getPublicAuthRedirect(pathname);
}

export function rewriteMarketingHref(pathname: string) {
  return getPublicAuthRedirect(pathname) ?? pathname;
}

export function getSitemapPublicPages() {
  return PUBLIC_ROUTE_CONFIGS.filter(
    (route) => route.canonicalPath !== "/blog"
  );
}

export function isSupportedMarketingLocale(
  locale: string
): locale is SupportedLanguage {
  return SUPPORTED_LANGUAGES.includes(locale as SupportedLanguage);
}
